/**
 * Pure display helpers for the member dashboard. They are member-local because
 * no other SPA shows a "Last synced" value or the one-month-later review date;
 * long dates ("August 1, 2026") come from the shared `fmtLongDate`.
 */

import { fmtLongDate } from "@dearbill/shared";

/**
 * The Medicare Connection "Last synced" value: "Today at 8:12 AM" when the
 * instant falls on the viewer's current local day, otherwise the long date.
 * Null or invalid input returns null, which omits the line.
 */
export function fmtSyncedAt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (d.toDateString() !== new Date().toDateString()) return fmtLongDate(iso);
  return `Today at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

/**
 * Relative day label for the timeline and mail rows: "Today", "Yesterday",
 * "N days ago" up to a week, then the long date. Calendar days are compared
 * in the viewer's local zone, so an event just before local midnight reads
 * "Yesterday" minutes later. Null or invalid input returns "—".
 */
export function fmtRelativeDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const startOfDay = (x: Date): number =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return fmtLongDate(iso);
}

/**
 * `savingsLastUpdatedAt` plus one calendar month: the next monthly eligibility
 * review that Home shows. Month-end clamps rather than overflows, so Jan 31
 * becomes Feb 28 or 29, never Mar 2 or 3. Null or invalid input returns null.
 */
export function plusOneMonth(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getUTCDate();
  d.setUTCMonth(d.getUTCMonth() + 1);
  // It overflowed into the following month (Jan 31 to Mar 3), so step back to the month end.
  if (d.getUTCDate() !== day) d.setUTCDate(0);
  return d.toISOString();
}
